/* eslint-disable spire/export-styles */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedCurrency from "@insite/client-framework/Common/Utilities/getLocalizedCurrency";
import { getUnitListPrice, getUnitNetPrice } from "@insite/client-framework/Services/Helpers/ProductPriceService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import { ProductPriceDto } from "@insite/client-framework/Types/ApiModels";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

type OwnProps = TypographyPresentationProps & {
    pricing: ProductPriceDto;
    showSavingsAmount: boolean;
    showSavingsPercent: boolean;
    currencySymbol: string;
    qtyOrdered?: number;
    unitOfMeasure?: string;
    savingPercentSetter?: (value: number) => void;
};

const mapStateToProps = (state: ApplicationState) => ({
    language: state.context.session.language,
});

type Props = ReturnType<typeof mapStateToProps> & OwnProps;


export interface ProductDetailsPriceStyles {
    wrapper?: InjectableCss;
    text?: InjectableCss;
    textWrapper?: InjectableCss;
}

export const priceStyles: ProductDetailsPriceStyles = {
    wrapper: {
       css:css`
        display: flex;
        align-items: left;
        flex-direction: column;
        gap: 0px;
        margin-bottom: 10px;
        line-height: 25px;
       `
    },
    text:{
        css: css`
        text-decoration: line-through;
        line-height: 25px;
        `
    },
    textWrapper: {
        css: css`
            display: flex;
            align-items: center;
            gap: 10px;
            font-size: 15px;
            color: #9E9E9E;
            font-style: normal;
            font-family: "Roboto-medium";
        `,
    },
 
};

const styles = priceStyles;
const ProductPriceSavingsMessage: FC<Props> = ({
    pricing,
    showSavingsAmount,
    showSavingsPercent,
    currencySymbol,
    qtyOrdered = 1,
    language,
    unitOfMeasure,
    savingPercentSetter,
    ...otherProps
}) => {
    const { price: unitNetPrice } = getUnitNetPrice(pricing, qtyOrdered);
    const { price: unitListPrice, priceDisplay: unitListPriceDisplay="2" } = getUnitListPrice(pricing, qtyOrdered);
    const hasPriceSavings = unitNetPrice < unitListPrice;
    if (!language || !pricing || !hasPriceSavings) {
        return null;
    } 

    const savingsAmount = Math.round((unitListPrice - unitNetPrice) * 100) / 100;
    const localizedSavingsAmount = getLocalizedCurrency({ amount: savingsAmount, language, currencySymbol });
    const savingsPercent = Math.round((savingsAmount / unitListPrice) * 100);
    React.useEffect(() => {
    if (savingsPercent && savingPercentSetter) {
        savingPercentSetter(savingsPercent);
    }
}, [savingsPercent, savingPercentSetter]);
    let message = "";
    if (showSavingsAmount && showSavingsPercent) {
        message = translate(
            "Regular Price: {0}, you save {1} ({2}%)",
            unitListPriceDisplay,
            localizedSavingsAmount,
            `${savingsPercent}`,
        );
    } else if (showSavingsPercent) {
        message = translate("Regular Price: {0}, you save {1}%", unitListPriceDisplay, `${savingsPercent}`);
    } else if (showSavingsAmount) {
        message = translate("Regular Price: {0}, you save {1}", unitListPriceDisplay, localizedSavingsAmount);
    } else {
        message = translate("Regular Price: {0}", unitListPriceDisplay);
    }

    return (
        <Typography {...otherProps} data-test-selector="productPriceSavingsMessage">
            <StyledWrapper {...styles.wrapper}>
                <StyledWrapper {...styles.textWrapper}>
                    <Typography>
                        {translate("Typical price:")}
                    </Typography> 
                    <Typography {...styles.text}>
                        {unitListPriceDisplay} / {unitOfMeasure}     
                    </Typography>
                </StyledWrapper>
                {/* {message} */}
            </StyledWrapper> 
        </Typography>
    );
};

export default connect(mapStateToProps)(ProductPriceSavingsMessage);
